import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { LocationType } from '@/types/firestore';

export type DiarySortField = 'date' | 'rating' | 'locationName' | 'drinkName' | 'brandName';
export type DiarySortDirection = 'asc' | 'desc';

export interface DiaryDateRange {
  start: string | null; // ISO string
  end: string | null;   // ISO string
}

export interface DiaryFiltersState {
  searchTerm: string;
  isPublic: 'all' | 'public' | 'private';
  minRating: number | null;
  maxRating: number | null;
  selectedBrandIds: string[];
  selectedFlavorIds: string[];
  selectedAttributeIds: string[];
  selectedLocationIds: string[];
  selectedLocationTypes: LocationType[];
  dateRange: DiaryDateRange;
  sortField: DiarySortField;
  sortDirection: DiarySortDirection;
  // viewMode: 'card' | 'table'; // If we add a view mode toggle later
}

const initialState: DiaryFiltersState = {
  searchTerm: '',
  isPublic: 'all',
  minRating: null,
  maxRating: null,
  selectedBrandIds: [],
  selectedFlavorIds: [],
  selectedAttributeIds: [],
  selectedLocationIds: [],
  selectedLocationTypes: [],
  dateRange: { start: null, end: null },
  sortField: 'date',
  sortDirection: 'desc',
  // viewMode: 'card',
};

export { initialState as diaryFiltersInitialState }; // Export initialState

const diaryFiltersSlice = createSlice({
  name: 'diaryFilters',
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setIsPublicFilter: (state, action: PayloadAction<'all' | 'public' | 'private'>) => {
      state.isPublic = action.payload;
    },
    setMinRating: (state, action: PayloadAction<number | null>) => {
      state.minRating = action.payload;
    },
    setMaxRating: (state, action: PayloadAction<number | null>) => {
      state.maxRating = action.payload;
    },
    toggleBrandId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedBrandIds.includes(id)) {
        state.selectedBrandIds = state.selectedBrandIds.filter(bId => bId !== id);
      } else {
        state.selectedBrandIds.push(id);
      }
    },
    setSelectedBrandIds: (state, action: PayloadAction<string[]>) => {
      state.selectedBrandIds = action.payload;
    },
    toggleFlavorId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedFlavorIds.includes(id)) {
        state.selectedFlavorIds = state.selectedFlavorIds.filter(fId => fId !== id);
      } else {
        state.selectedFlavorIds.push(id);
      }
    },
    setSelectedFlavorIds: (state, action: PayloadAction<string[]>) => {
      state.selectedFlavorIds = action.payload;
    },
    toggleAttributeId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedAttributeIds.includes(id)) {
        state.selectedAttributeIds = state.selectedAttributeIds.filter(aId => aId !== id);
      } else {
        state.selectedAttributeIds.push(id);
      }
    },
    setSelectedAttributeIds: (state, action: PayloadAction<string[]>) => {
      state.selectedAttributeIds = action.payload;
    },
    toggleLocationId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedLocationIds.includes(id)) {
        state.selectedLocationIds = state.selectedLocationIds.filter(lId => lId !== id);
      } else {
        state.selectedLocationIds.push(id);
      }
    },
    setSelectedLocationIds: (state, action: PayloadAction<string[]>) => {
      state.selectedLocationIds = action.payload;
    },
    toggleLocationType: (state, action: PayloadAction<LocationType>) => {
      const type = action.payload;
      if (state.selectedLocationTypes.includes(type)) {
        state.selectedLocationTypes = state.selectedLocationTypes.filter(lt => lt !== type);
      } else {
        state.selectedLocationTypes.push(type);
      }
    },
    setSelectedLocationTypes: (state, action: PayloadAction<LocationType[]>) => {
      state.selectedLocationTypes = action.payload;
    },
    setDateRange: (state, action: PayloadAction<DiaryDateRange>) => {
      state.dateRange = action.payload;
    },
    setSortField: (state, action: PayloadAction<DiarySortField>) => {
      state.sortField = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<DiarySortDirection>) => {
      state.sortDirection = action.payload;
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    resetDiaryFilters: () => initialState,
  }
});

export const {
  setSearchTerm,
  setIsPublicFilter,
  setMinRating,
  setMaxRating,
  toggleBrandId, setSelectedBrandIds,
  toggleFlavorId, setSelectedFlavorIds,
  toggleAttributeId, setSelectedAttributeIds,
  toggleLocationId, setSelectedLocationIds,
  toggleLocationType, setSelectedLocationTypes,
  setDateRange,
  setSortField,
  setSortDirection,
  toggleSortDirection,
  resetDiaryFilters,
} = diaryFiltersSlice.actions;

// Selectors
export const selectDiarySearchTerm = (state: RootState) => state.diaryFilters.searchTerm;
export const selectDiaryIsPublicFilter = (state: RootState) => state.diaryFilters.isPublic;
export const selectDiaryMinRating = (state: RootState) => state.diaryFilters.minRating;
export const selectDiaryMaxRating = (state: RootState) => state.diaryFilters.maxRating;
export const selectDiarySelectedBrandIds = (state: RootState) => state.diaryFilters.selectedBrandIds;
export const selectDiarySelectedFlavorIds = (state: RootState) => state.diaryFilters.selectedFlavorIds;
export const selectDiarySelectedAttributeIds = (state: RootState) => state.diaryFilters.selectedAttributeIds;
export const selectDiarySelectedLocationIds = (state: RootState) => state.diaryFilters.selectedLocationIds;
export const selectDiarySelectedLocationTypes = (state: RootState) => state.diaryFilters.selectedLocationTypes;
export const selectDiaryDateRange = (state: RootState) => state.diaryFilters.dateRange;
export const selectDiarySortField = (state: RootState) => state.diaryFilters.sortField;
export const selectDiarySortDirection = (state: RootState) => state.diaryFilters.sortDirection;
export const selectAllDiaryFilters = (state: RootState) => state.diaryFilters; // Selector for all filters

export default diaryFiltersSlice.reducer; 